import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export type TabType = 'home' | 'queue' | 'document';

export interface WorkspaceTab {
  id: string;
  type: TabType;
  title: string;
  resourceId?: string; // Queue ID or Document ID
  icon?: string;
}

interface WorkspaceTabsContextType {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  openTab: (tab: Omit<WorkspaceTab, 'id'>) => string;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTabTitle: (tabId: string, title: string) => void;
  reorderTabs: (oldIndex: number, newIndex: number) => void;
  closeOtherTabs: (tabId: string) => void;
  closeTabsToRight: (tabId: string) => void;
  getTabByResourceId: (type: TabType, resourceId: string) => WorkspaceTab | undefined;
  organizationId: string | null;
  setOrganizationId: (id: string | null) => void;
}

const WorkspaceTabsContext = createContext<WorkspaceTabsContextType | undefined>(undefined);

const generateTabId = () => `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

const getStorageKey = (orgId: string) => `workspace-tabs-${orgId}`;

export function WorkspaceTabsProvider({ children }: { children: ReactNode }) {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [tabs, setTabs] = useState<WorkspaceTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  // Load tabs from localStorage when organization changes
  useEffect(() => {
    if (!organizationId) {
      setTabs([]);
      setActiveTabId(null);
      return;
    }

    const storageKey = getStorageKey(organizationId);
    const saved = localStorage.getItem(storageKey);
    
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setTabs(parsed.tabs || []);
        setActiveTabId(parsed.activeTabId || null);
      } catch {
        // Initialize with home tab if parse fails
        const homeTab: WorkspaceTab = {
          id: generateTabId(),
          type: 'home',
          title: 'Workspace Home',
        };
        setTabs([homeTab]);
        setActiveTabId(homeTab.id);
      }
    } else {
      // Initialize with home tab for new workspaces
      const homeTab: WorkspaceTab = {
        id: generateTabId(),
        type: 'home',
        title: 'Workspace Home',
      };
      setTabs([homeTab]);
      setActiveTabId(homeTab.id);
    }
  }, [organizationId]);

  // Save tabs to localStorage when they change
  useEffect(() => {
    if (!organizationId || tabs.length === 0) return;

    const storageKey = getStorageKey(organizationId);
    localStorage.setItem(storageKey, JSON.stringify({
      tabs,
      activeTabId,
    }));
  }, [tabs, activeTabId, organizationId]);

  const getTabByResourceId = useCallback((type: TabType, resourceId: string) => {
    return tabs.find(tab => tab.type === type && tab.resourceId === resourceId);
  }, [tabs]);

  const openTab = useCallback((tabData: Omit<WorkspaceTab, 'id'>) => {
    // Check if tab already exists for this resource
    if (tabData.resourceId) {
      const existingTab = tabs.find(
        tab => tab.type === tabData.type && tab.resourceId === tabData.resourceId
      );
      if (existingTab) {
        setActiveTabId(existingTab.id);
        return existingTab.id;
      }
    }

    // Check for home tab
    if (tabData.type === 'home') {
      const existingHome = tabs.find(tab => tab.type === 'home');
      if (existingHome) {
        setActiveTabId(existingHome.id);
        return existingHome.id;
      }
    }

    const newTab: WorkspaceTab = {
      ...tabData,
      id: generateTabId(),
    };

    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
    return newTab.id;
  }, [tabs]);

  const closeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const tabToClose = prev.find(tab => tab.id === tabId);
      
      // Don't allow closing the home tab
      if (tabToClose?.type === 'home') return prev;
      
      const newTabs = prev.filter(tab => tab.id !== tabId);
      
      // If we're closing the active tab, switch to home
      if (activeTabId === tabId) {
        const homeTab = newTabs.find(tab => tab.type === 'home');
        if (homeTab) {
          setActiveTabId(homeTab.id);
        }
      }

      return newTabs;
    });
  }, [activeTabId]);

  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const updateTabTitle = useCallback((tabId: string, title: string) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, title } : tab
    ));
  }, []);

  const reorderTabs = useCallback((oldIndex: number, newIndex: number) => {
    setTabs(prev => {
      const newTabs = [...prev];
      const [removed] = newTabs.splice(oldIndex, 1);
      newTabs.splice(newIndex, 0, removed);
      return newTabs;
    });
  }, []);

  const closeOtherTabs = useCallback((tabId: string) => {
    setTabs(prev => {
      const tabToKeep = prev.find(tab => tab.id === tabId);
      const homeTab = prev.find(tab => tab.type === 'home');
      
      // Always keep the home tab
      if (tabToKeep?.type === 'home') {
        return [tabToKeep];
      }
      
      // Keep both home and the selected tab
      const newTabs = prev.filter(tab => tab.id === tabId || tab.type === 'home');
      return newTabs;
    });
    setActiveTabId(tabId);
  }, []);

  const closeTabsToRight = useCallback((tabId: string) => {
    setTabs(prev => {
      const index = prev.findIndex(tab => tab.id === tabId);
      if (index === -1) return prev;
      // Keep tabs up to and including the selected one, but always keep home tab
      const keptTabs = prev.slice(0, index + 1);
      const homeTab = prev.find(tab => tab.type === 'home');
      if (homeTab && !keptTabs.find(t => t.type === 'home')) {
        return [homeTab, ...keptTabs];
      }
      return keptTabs;
    });
  }, []);

  return (
    <WorkspaceTabsContext.Provider
      value={{
        tabs,
        activeTabId,
        openTab,
        closeTab,
        setActiveTab,
        updateTabTitle,
        reorderTabs,
        closeOtherTabs,
        closeTabsToRight,
        getTabByResourceId,
        organizationId,
        setOrganizationId,
      }}
    >
      {children}
    </WorkspaceTabsContext.Provider>
  );
}

export function useWorkspaceTabs() {
  const context = useContext(WorkspaceTabsContext);
  if (!context) {
    throw new Error('useWorkspaceTabs must be used within a WorkspaceTabsProvider');
  }
  return context;
}
